import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
  FocusEvent,
  ChangeEvent,
} from 'react'

import {
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react'
import { clsx } from 'clsx'

import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

export interface TimePickerProps {
  value?: string
  onChange?: (value: string) => void
  minTime?: string
  maxTime?: string
  interval?: number
  className?: string
}

interface TimeOption {
  value: string
  label: string
}

/**
 * Converts minutes since midnight to HH:mm format
 */
function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
}

/**
 * Converts HH:mm format to minutes since midnight
 */
function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

/**
 * Parses and normalizes user input to a valid time string (24-hour format)
 * Handles various formats like "9", "9:30", "930", "09:30"
 * Auto-corrects invalid values by clamping to valid ranges
 */
function parseTimeInput(input: string): string | null {
  if (!input) {
    return null
  }

  const cleaned = input.trim()
  // Extract only digits and colon
  const numericPart = cleaned.replace(/[^0-9:]/g, '')

  // If no numeric content, can't parse
  if (!numericPart || !/\d/.test(numericPart)) {
    return null
  }

  let hours: number
  let minutes: number

  // Try to parse different formats
  if (numericPart.includes(':')) {
    // Format: H:MM or HH:MM
    const parts = numericPart.split(':')
    hours = parseInt(parts[0], 10) || 0
    minutes = parseInt(parts[1], 10) || 0
  } else if (numericPart.length <= 2) {
    // Format: H or HH (hours only)
    hours = parseInt(numericPart, 10) || 0
    minutes = 0
  } else if (numericPart.length === 3) {
    // Format: HMM (e.g., "930" -> 9:30)
    hours = parseInt(numericPart[0], 10) || 0
    minutes = parseInt(numericPart.slice(1), 10) || 0
  } else if (numericPart.length >= 4) {
    // Format: HHMM or longer (e.g., "0930" -> 09:30, "1244" -> 12:44)
    hours = parseInt(numericPart.slice(0, 2), 10) || 0
    minutes = parseInt(numericPart.slice(2, 4), 10) || 0
  } else {
    return null
  }

  // Clamp to valid ranges (auto-correct invalid values)
  hours = Math.min(23, Math.max(0, hours))
  minutes = Math.min(59, Math.max(0, minutes))

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

/**
 * Finds the index of the closest time option to the given time
 */
function findClosestTimeIndex(options: TimeOption[], time?: string): number {
  if (options.length === 0 || !time) {
    return 0
  }

  const targetMinutes = timeToMinutes(time)
  let closestIndex = 0
  let smallestDiff = Infinity

  for (let i = 0; i < options.length; i++) {
    const optionMinutes = timeToMinutes(options[i].value)
    const diff = Math.abs(optionMinutes - targetMinutes)
    if (diff < smallestDiff) {
      smallestDiff = diff
      closestIndex = i
    }
  }

  return closestIndex
}

/**
 * Generates time options for the dropdown
 */
function generateTimeOptions(
  interval: number,
  minTime?: string,
  maxTime?: string,
): TimeOption[] {
  const options: TimeOption[] = []
  const minMinutes = minTime ? timeToMinutes(minTime) : 0
  const maxMinutes = maxTime ? timeToMinutes(maxTime) : 24 * 60 - interval

  for (let minutes = 0; minutes < 24 * 60; minutes += interval) {
    const time = minutesToTime(minutes)
    const isBeforeMinTime = minutes < minMinutes || minutes > maxMinutes
    if (!isBeforeMinTime) {
      options.push({
        value: time,
        label: time,
      })
    }
  }

  return options
}

export const TimePicker = forwardRef<HTMLInputElement, TimePickerProps>(
  (
    { value = '', onChange, minTime, maxTime, interval = 15, className },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [inputValue, setInputValue] = useState(value)
    const [activeIndex, setActiveIndex] = useState<number | null>(null)
    const listRef = useRef<(HTMLButtonElement | null)[]>([])
    const inputRef = useRef<HTMLInputElement>(null)
    const listboxId = useId()

    const timeOptions = useMemo(
      () => generateTimeOptions(interval, minTime, maxTime),
      [interval, minTime, maxTime],
    )

    const currentValueIndex = useMemo(() => {
      const index = timeOptions.findIndex(opt => opt.value === value)
      return index >= 0 ? index : null
    }, [timeOptions, value])

    useEffect(() => {
      setInputValue(value)
    }, [value])

    const onOpenChange = useCallback(
      (open: boolean) => {
        setIsOpen(open)
        if (open) {
          /**
           * The focusItemOnOpen can be passed to the useListNavigation hook but the scroll
           * position is not correct and the selected value is placed at the bottom of the list.
           * We want it to be at the top of the list.
           */
          requestAnimationFrame(() => {
            const scrollToIndex = findClosestTimeIndex(timeOptions, value)
            const scrollToEl = listRef.current[scrollToIndex]
            if (scrollToEl) {
              scrollToEl.parentElement?.scrollTo({
                top: scrollToEl.offsetTop - scrollToEl.offsetHeight / 2,
              })
            }
          })
        }
      },
      [timeOptions, value],
    )

    const { refs, context } = useFloating({
      open: isOpen,
      onOpenChange,
      placement: 'bottom-end',
    })

    const dismiss = useDismiss(context, {
      referencePress: false,
    })
    const role = useRole(context, { role: 'listbox' })
    const listNavigation = useListNavigation(context, {
      listRef,
      activeIndex,
      selectedIndex: currentValueIndex,
      onNavigate: setActiveIndex,
    })

    const { getFloatingProps, getItemProps } = useInteractions([
      dismiss,
      role,
      listNavigation,
    ])

    const handleInputChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value
        const filtered = newValue.replace(/[^0-9:]/g, '')
        setInputValue(filtered)
      },
      [],
    )

    const handleBlur = useCallback(
      (e: FocusEvent<HTMLInputElement>) => {
        if (
          e.relatedTarget &&
          refs.floating.current?.contains(e.relatedTarget as Node)
        ) {
          return
        }

        const trimmedInput = inputValue.trim()

        if (!trimmedInput) {
          /**
           * Avoid having an empty input value when the user blurs the input.
           */
          setInputValue(value)
          return
        }

        const parsed = parseTimeInput(trimmedInput)

        if (parsed) {
          let finalTime = parsed

          // Clamp to min/max
          if (minTime && timeToMinutes(parsed) < timeToMinutes(minTime)) {
            finalTime = minTime
          }
          if (maxTime && timeToMinutes(parsed) > timeToMinutes(maxTime)) {
            finalTime = maxTime
          }

          setInputValue(finalTime)
          onChange?.(finalTime)
        } else {
          const numbersOnly = trimmedInput.replace(/[^0-9]/g, '')
          if (numbersOnly.length > 0) {
            // Try parsing just the numbers
            const salvaged = parseTimeInput(numbersOnly)
            if (salvaged) {
              let finalTime = salvaged
              if (minTime && timeToMinutes(salvaged) < timeToMinutes(minTime)) {
                finalTime = minTime
              }
              if (maxTime && timeToMinutes(salvaged) > timeToMinutes(maxTime)) {
                finalTime = maxTime
              }
              setInputValue(finalTime)
              onChange?.(finalTime)
              return
            }
          }
          // Truly invalid - revert to previous valid value or keep showing what user typed
          if (value) {
            setInputValue(value)
          }
        }
      },
      [inputValue, value, onChange, minTime, maxTime, refs.floating],
    )

    const handleSelect = useCallback(
      (time: string) => {
        setInputValue(time)
        onChange?.(time)
        setIsOpen(false)
        inputRef.current?.focus()
      },
      [onChange],
    )

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault()

          if (isOpen && activeIndex !== null) {
            const option = timeOptions[activeIndex]
            if (option) {
              handleSelect(option.value)
            }
          } else {
            // Validate and save current input
            const parsed = parseTimeInput(inputValue)
            if (parsed) {
              let finalTime = parsed
              if (minTime && timeToMinutes(parsed) < timeToMinutes(minTime)) {
                finalTime = minTime
              }
              if (maxTime && timeToMinutes(parsed) > timeToMinutes(maxTime)) {
                finalTime = maxTime
              }
              setInputValue(finalTime)
              onChange?.(finalTime)
            }
            setIsOpen(false)
          }
        } else if (e.key === 'ArrowDown' && !isOpen) {
          onOpenChange(true)
        }
      },
      [
        isOpen,
        activeIndex,
        timeOptions,
        handleSelect,
        inputValue,
        minTime,
        maxTime,
        onChange,
        onOpenChange,
      ],
    )

    return (
      <div className={clsx('relative w-full h-full', className)}>
        <div
          ref={refs.setReference}
          className={clsx(
            'flex items-center gap-1 h-full',
            'focus-within:outline-none ring-offset-0',
          )}
          onClick={() => onOpenChange(!isOpen)}
        >
          <SvgIcon size="md" name="clock" className={clsx('shrink-0')} />
          <input
            ref={ref}
            type="text"
            role="combobox"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-autocomplete="none"
            aria-controls={listboxId}
            className={clsx(
              'grow appearance-none',
              'focus-visible:outline-none bg-transparent',
              'min-w-0',
            )}
          />
        </div>

        {isOpen && (
          <div
            ref={refs.setFloating}
            id={listboxId}
            role="listbox"
            tabIndex={-1}
            className={clsx(
              // Ideally, could use the floatingStyles from the useFloating hook but
              // it causes a ui shift on small screens (it changes the transform origin during render).
              // The translate here is a workaround to prevent the ui shift.
              'z-50 no-scrollbar absolute -translate-x-1/3',
              'p-1.5 rounded-popover shadow-popover bg-surface',
              'border border-line-subdued',
              'animate-fade-in duration-100',
              'overflow-y-auto',
              'max-h-[280px]',
            )}
            {...getFloatingProps()}
            onMouseDown={e => e.preventDefault()}
          >
            {timeOptions.map((option, index) => (
              <button
                key={option.value}
                ref={node => {
                  listRef.current[index] = node
                }}
                type="button"
                role="option"
                aria-selected={option.value === value}
                tabIndex={-1}
                className={clsx(
                  'w-full px-8 py-1 my-1 rounded-base text-center',
                  'focus:outline-none',
                  'text-content',
                  activeIndex === index && 'bg-surface-hovered',
                  option.value === value &&
                    activeIndex !== index &&
                    'bg-surface-pressed',
                  activeIndex !== index &&
                    option.value !== value &&
                    'hover:bg-surface-hovered',
                )}
                {...getItemProps({
                  onClick: () => {
                    handleSelect(option.value)
                  },
                })}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  },
)
